/**
 * Модуль из конструктора мебели — одна вещь под размер: шаблоны и разбор на детали.
 *
 * Корпус собирается из плит 18 мм (боковины, крышка, дно, задняя стенка, перегородки
 * между секциями), поэтому открытые полки видно внутрь. Фасады накладные, во всю
 * секцию; ящики — стопкой равных фасадов. Детали — в метрах, центр модуля в начале
 * координат, перед — к +Z; их же рисует вид спереди на развёртке.
 */
import {
	FrontHinge,
	FurnitureModuleType,
	ModuleBase,
	ModuleHandle,
	ModuleSectionFill,
} from "@/api";
import { FURNITURE_BODY_COLORS, FURNITURE_FRONT_COLORS } from "./colors";
import { DOOR_ANGLE, METERS_PER_MM, MODULE } from "./constants";
import { swingAngle, type PartMotion } from "./motion";
import {
	KitchenPartMaterial,
	KitchenPartShape,
	type KitchenPart,
	type ModuleDesign,
} from "./types";

const {
	board: BOARD,
	back: BACK,
	gap: GAP,
	front: FRONT,
	base: BASE_HEIGHT,
	topDrawer: TOP_DRAWER,
	singleDoorMax: SINGLE_DOOR_MAX,
} = MODULE;
const MM = METERS_PER_MM;

/** В секции одна дверца — у неё можно перевесить петли */
export const isSingleDoorModuleSection = (design: ModuleDesign, index: number) => {
	const fill = design.sections[index]?.fill;
	if (fill !== ModuleSectionFill.Doors && fill !== ModuleSectionFill.DrawerDoors) return false;
	const lines = moduleSectionLines(design);
	return lines[index + 1] - lines[index] <= SINGLE_DOOR_MAX;
};

/** Петли одной дверцы секции: заданные или к ближнему краю модуля (у правой секции — справа) */
export const moduleSectionHinge = (design: ModuleDesign, index: number): FrontHinge =>
	design.sections[index]?.hinge ??
	(index === design.sections.length - 1 && design.sections.length > 1
		? FrontHinge.Right
		: FrontHinge.Left);

export const MODULE_FRONT_COLORS = FURNITURE_FRONT_COLORS;

export const MODULE_BODY_COLORS = FURNITURE_BODY_COLORS;

export const MODULE_FILL_LABELS: Record<ModuleSectionFill, string> = {
	[ModuleSectionFill.Doors]: "Двери",
	[ModuleSectionFill.Drawers]: "Ящики",
	[ModuleSectionFill.DrawerDoors]: "Ящик + двери",
	[ModuleSectionFill.Open]: "Открытая",
};

/** Шаблон модуля: всё, кроме id и времени сохранения */
export type ModuleTemplate = Omit<ModuleDesign, "id" | "updatedAt">;

const white = MODULE_FRONT_COLORS.Белый;

export const MODULE_TEMPLATES: ModuleTemplate[] = [
	{
		name: "Комод",
		type: FurnitureModuleType.Floor,
		width: 800,
		height: 850,
		depth: 450,
		elevation: 0,
		sections: [{ fill: ModuleSectionFill.Drawers, hinge: null }],
		drawers: 4,
		shelves: 0,
		base: ModuleBase.Legs,
		handle: ModuleHandle.Bar,
		frontColor: white,
		bodyColor: white,
	},
	{
		name: "Тумба",
		type: FurnitureModuleType.Floor,
		width: 600,
		height: 800,
		depth: 450,
		elevation: 0,
		sections: [{ fill: ModuleSectionFill.DrawerDoors, hinge: null }],
		drawers: 2,
		shelves: 1,
		base: ModuleBase.Plinth,
		handle: ModuleHandle.Bar,
		frontColor: white,
		bodyColor: white,
	},
	{
		name: "Тумба под ТВ",
		type: FurnitureModuleType.Floor,
		width: 1600,
		height: 500,
		depth: 400,
		elevation: 0,
		sections: [
			{ fill: ModuleSectionFill.Doors, hinge: null },
			{ fill: ModuleSectionFill.Open, hinge: null },
			{ fill: ModuleSectionFill.Doors, hinge: null },
		],
		drawers: 2,
		shelves: 1,
		base: ModuleBase.Legs,
		handle: ModuleHandle.None,
		frontColor: MODULE_FRONT_COLORS.Графит,
		bodyColor: MODULE_BODY_COLORS.Дуб,
	},
	{
		name: "Тумба под раковину",
		type: FurnitureModuleType.Floor,
		width: 600,
		height: 820,
		depth: 460,
		elevation: 0,
		sections: [{ fill: ModuleSectionFill.Doors, hinge: null }],
		drawers: 2,
		shelves: 0,
		base: ModuleBase.Legs,
		handle: ModuleHandle.Bar,
		frontColor: white,
		bodyColor: white,
	},
	{
		name: "Пенал",
		type: FurnitureModuleType.Tall,
		width: 400,
		height: 2000,
		depth: 450,
		elevation: 0,
		sections: [{ fill: ModuleSectionFill.Doors, hinge: null }],
		drawers: 2,
		shelves: 5,
		base: ModuleBase.Plinth,
		handle: ModuleHandle.Bar,
		frontColor: white,
		bodyColor: white,
	},
	{
		name: "Навесной шкаф",
		type: FurnitureModuleType.Wall,
		width: 800,
		height: 700,
		depth: 330,
		elevation: 1450,
		sections: [{ fill: ModuleSectionFill.Doors, hinge: null }],
		drawers: 2,
		shelves: 1,
		base: ModuleBase.None,
		handle: ModuleHandle.Bar,
		frontColor: white,
		bodyColor: white,
	},
	{
		name: "Стеллаж",
		type: FurnitureModuleType.Shelving,
		width: 800,
		height: 1800,
		depth: 350,
		elevation: 0,
		sections: [{ fill: ModuleSectionFill.Open, hinge: null }],
		drawers: 2,
		shelves: 4,
		base: ModuleBase.None,
		handle: ModuleHandle.None,
		frontColor: MODULE_BODY_COLORS.Дуб,
		bodyColor: MODULE_BODY_COLORS.Дуб,
	},
	{
		name: "Обувница",
		type: FurnitureModuleType.Floor,
		width: 800,
		height: 1000,
		depth: 320,
		elevation: 0,
		sections: [
			{ fill: ModuleSectionFill.Doors, hinge: null },
			{ fill: ModuleSectionFill.Doors, hinge: null },
		],
		drawers: 2,
		shelves: 3,
		base: ModuleBase.Plinth,
		handle: ModuleHandle.Knob,
		frontColor: white,
		bodyColor: white,
	},
];

/** Границы секций модуля по ширине от левого края, мм: края и середины перегородок */
export const moduleSectionLines = (design: ModuleDesign) => {
	const count = Math.max(1, design.sections.length);
	const cell = (design.width - 2 * BOARD - (count - 1) * BOARD) / count;
	return [
		0,
		...Array.from({ length: count - 1 }, (_, i) => BOARD + (i + 1) * (cell + BOARD) - BOARD / 2),
		design.width,
	];
};

/** Высота основания модуля (цоколь или ножки), мм; у навесного и без основания — 0 */
export const moduleBaseHeight = (design: ModuleDesign) =>
	design.type !== FurnitureModuleType.Wall && design.base !== ModuleBase.None ? BASE_HEIGHT : 0;

/** Детали модуля: корпус из плит, полки, фасады, ручки, цоколь или ножки */
export const moduleParts = (design: ModuleDesign): KitchenPart[] => {
	const { width: W, height: H, depth: D } = design;
	const parts: KitchenPart[] = [];
	const body = KitchenPartMaterial.Body;
	/** Брусок по пределам x (от левого края), y (от низа модуля), z (от задней стенки), мм */
	const box = (
		[x0, x1]: readonly [number, number],
		[y0, y1]: readonly [number, number],
		[z0, z1]: readonly [number, number],
		material: KitchenPartMaterial,
		shadow = false,
	) =>
		parts.push({
			shape: KitchenPartShape.Box,
			position: {
				x: ((x0 + x1) / 2 - W / 2) * MM,
				y: ((y0 + y1) / 2 - H / 2) * MM,
				z: ((z0 + z1) / 2 - D / 2) * MM,
			},
			size: { x: (x1 - x0) * MM, y: (y1 - y0) * MM, z: (z1 - z0) * MM },
			material,
			shadow,
		});

	/** Всё, что нарисует `draw`, движется как одна створка или ящик */
	let fronts = 0;
	const moving = (motion: PartMotion, draw: () => void) => {
		const start = parts.length;
		draw();
		const front = `front/${fronts++}`;
		for (let i = start; i < parts.length; i++) {
			parts[i].motion = motion;
			parts[i].front = front;
		}
	};

	const onFloor = design.type !== FurnitureModuleType.Wall;
	const bottom = moduleBaseHeight(design);
	const bodyDepth = D - FRONT;
	const closedFront = design.sections.some((section) => section.fill !== ModuleSectionFill.Open);
	const carcassDepth = closedFront ? bodyDepth : D;

	// Основание: цоколь утоплен от фасада, ножки — по углам
	if (onFloor && design.base === ModuleBase.Plinth) {
		// Цоколь — цвета корпуса: у отдельной мебели он не тёмный, как у кухни
		box([BOARD, W - BOARD], [0, bottom], [20, carcassDepth - 40], body);
	} else if (onFloor && design.base === ModuleBase.Legs) {
		for (const x of [60, W - 60]) {
			for (const z of [60, carcassDepth - 60]) {
				parts.push({
					shape: KitchenPartShape.Cylinder,
					position: { x: (x - W / 2) * MM, y: (bottom / 2 - H / 2) * MM, z: (z - D / 2) * MM },
					size: { x: 0.015, y: bottom * MM, z: 0.015 },
					material: KitchenPartMaterial.Steel,
				});
			}
		}
	}

	// Корпус: боковины, крышка, дно, задняя стенка
	box([0, BOARD], [bottom, H], [0, carcassDepth], body, true);
	box([W - BOARD, W], [bottom, H], [0, carcassDepth], body, true);
	box([BOARD, W - BOARD], [H - BOARD, H], [0, carcassDepth], KitchenPartMaterial.Worktop, true);
	box([BOARD, W - BOARD], [bottom, bottom + BOARD], [0, carcassDepth], body);
	box([BOARD, W - BOARD], [bottom + BOARD, H - BOARD], [0, BACK], body);

	const count = Math.max(1, design.sections.length);
	const inner = W - 2 * BOARD - (count - 1) * BOARD;
	const cell = inner / count;
	const innerBottom = bottom + BOARD;
	const innerTop = H - BOARD;

	design.sections.forEach(({ fill }, index) => {
		const x0 = BOARD + index * (cell + BOARD);
		const x1 = x0 + cell;
		if (index > 0) box([x0 - BOARD, x0], [innerBottom, innerTop], [BACK, carcassDepth], body);
		// Фасад накладной: до середины перегородки, крайние — до края корпуса
		const f0 = index === 0 ? 0 : x0 - BOARD / 2;
		const f1 = index === count - 1 ? W : x1 + BOARD / 2;
		const frontZ = [bodyDepth, bodyDepth + FRONT] as const;
		const handleZ = [bodyDepth + FRONT, bodyDepth + FRONT + 20] as const;

		const panel = (
			u0: number,
			u1: number,
			v0: number,
			v1: number,
			handle: "side-right" | "side-left" | "top" | "bottom",
		) => {
			box([u0 + GAP, u1 - GAP], [v0 + GAP, v1 - GAP], frontZ, KitchenPartMaterial.Front);
			if (design.handle === ModuleHandle.None) return;
			const isKnob = design.handle === ModuleHandle.Knob;
			const mid = (v0 + v1) / 2;
			if (handle === "top" || handle === "bottom") {
				const hy = handle === "top" ? v1 - 45 : v0 + 45;
				const cu = (u0 + u1) / 2;
				const span = isKnob ? 24 : Math.min(160, (u1 - u0) * 0.45);
				box(
					[cu - span / 2, cu + span / 2],
					[hy - (isKnob ? 12 : 6), hy + (isKnob ? 12 : 6)],
					handleZ,
					KitchenPartMaterial.Handle,
				);
			} else {
				const hu = handle === "side-right" ? u1 - 40 : u0 + 40;
				const hy0 = design.type === FurnitureModuleType.Wall ? v0 + 40 : Math.min(mid, v1 - 200);
				const span = isKnob ? 24 : Math.min(160, (v1 - v0) * 0.5);
				box(
					[hu - (isKnob ? 12 : 6), hu + (isKnob ? 12 : 6)],
					[hy0, hy0 + span],
					handleZ,
					KitchenPartMaterial.Handle,
				);
			}
		};
		/** Дверца на петлях у края `x` (мм от левого края) — открывается наружу */
		const door = (u0: number, u1: number, v0: number, v1: number, hingeRight: boolean) => {
			const pivotX = hingeRight ? u1 : u0;
			const motion: PartMotion = {
				kind: "hinge",
				axis: "y",
				pivot: [(pivotX - W / 2) * MM, 0, (bodyDepth + FRONT - D / 2) * MM],
				angle: swingAngle([hingeRight ? -1 : 1, 0, 0], [0, 0, 1], "y", DOOR_ANGLE),
			};
			moving(motion, () => panel(u0, u1, v0, v1, hingeRight ? "side-left" : "side-right"));
		};
		const doorsIn = (v0: number, v1: number) => {
			if (f1 - f0 <= SINGLE_DOOR_MAX) {
				door(f0, f1, v0, v1, moduleSectionHinge(design, index) === FrontHinge.Right);
				return;
			}
			const mid = (f0 + f1) / 2;
			door(f0, mid, v0, v1, false);
			door(mid, f1, v0, v1, true);
		};
		/** Ящик: фасад и короб за ним выезжают вместе */
		const travel = Math.min(MODULE.drawerTravel, bodyDepth * 0.75) * MM;
		const drawer = (v0: number, v1: number) =>
			moving({ kind: "slide", offset: [0, 0, travel] }, () => {
				panel(f0, f1, v0, v1, "top");
				const low = v0 + GAP + 20;
				const high = Math.max(low + 40, v1 - 40);
				const depth = [BACK + 20, bodyDepth] as const;
				box([x0 + 10, x0 + 22], [low, high], depth, body);
				box([x1 - 22, x1 - 10], [low, high], depth, body);
				box([x0 + 10, x1 - 10], [low, high], [depth[0], depth[0] + 12], body);
				box([x0 + 10, x1 - 10], [low, low + 12], depth, body);
			});
		const shelvesIn = (v0: number, v1: number) => {
			for (let shelf = 1; shelf <= design.shelves; shelf++) {
				const y = v0 + ((v1 - v0) * shelf) / (design.shelves + 1);
				box([x0, x1], [y - BOARD / 2, y + BOARD / 2], [BACK, carcassDepth - 20], body);
			}
		};

		const frontBottom = bottom;
		if (fill === ModuleSectionFill.Open) {
			shelvesIn(innerBottom, innerTop);
		} else if (fill === ModuleSectionFill.Drawers) {
			const n = Math.max(1, design.drawers);
			const step = (H - frontBottom) / n;
			for (let i = 0; i < n; i++) drawer(frontBottom + step * i, frontBottom + step * (i + 1));
		} else if (fill === ModuleSectionFill.DrawerDoors) {
			drawer(H - TOP_DRAWER, H);
			box(
				[x0, x1],
				[H - TOP_DRAWER - BOARD / 2, H - TOP_DRAWER + BOARD / 2],
				[BACK, carcassDepth],
				body,
			);
			shelvesIn(innerBottom, H - TOP_DRAWER);
			doorsIn(frontBottom, H - TOP_DRAWER);
		} else {
			shelvesIn(innerBottom, innerTop);
			doorsIn(frontBottom, H);
		}
	});
	return parts;
};

/** Новый модуль из шаблона */
export const moduleFromTemplate = (
	template: ModuleTemplate,
	id: string,
): Omit<ModuleDesign, "updatedAt"> => ({
	...template,
	sections: template.sections.map((section) => ({ ...section })),
	id,
});
